import type { fabric } from "fabric";
import type { CanvasInsertionObject } from "../types";
import { isBottomGenerationAnchor } from "../anchorEligibility";

/** 读取持久化的插入顺序；历史图片尚为骨架时读取原始对象。 */
export function getCanvasInsertionOrder(obj: CanvasInsertionObject): number {
  const value = obj.canvasInsertionOrder ?? obj.data?.originalObj?.canvasInsertionOrder;
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
}

function stampInsertionOrder(obj: CanvasInsertionObject, order: number): void {
  obj.canvasInsertionOrder = order;
  if (obj.data?.originalObj) obj.data.originalObj.canvasInsertionOrder = order;
}

/** 在历史对象上屏前补齐顺序，避免首次点击置顶污染后续生图锚点。 */
export function initializeCanvasInsertionOrder(objects: CanvasInsertionObject[]): void {
  let order = objects.reduce((max, obj) => Math.max(max, getCanvasInsertionOrder(obj)), 0);
  // 旧快照只有层级顺序时，已有底部游标标记比被点击改写的层级更可靠。
  const legacyCursor = order === 0
    ? objects.find((obj) => obj.isBottomGenerateCursor && isBottomGenerationAnchor(obj))
    : undefined;
  for (const obj of objects) {
    stampInsertionOrder(obj, getCanvasInsertionOrder(obj) || ++order);
  }
  if (legacyCursor) stampInsertionOrder(legacyCursor, ++order);
}

/** object:added 时记录新插入；恢复/替换保留顺序，复制的新对象分配新顺序。 */
export function recordCanvasObjectInsertion(canvas: fabric.Canvas, obj: CanvasInsertionObject): void {
  const objects = canvas.getObjects();
  const order = getCanvasInsertionOrder(obj);
  const duplicate = order > 0 && objects.some((other) =>
    other !== obj && other.id !== obj.id && getCanvasInsertionOrder(other) === order,
  );
  if (order && !duplicate) return;
  const latest = objects.reduce((max, other) => Math.max(max, getCanvasInsertionOrder(other)), 0);
  stampInsertionOrder(obj, latest + 1);
}

/** 占位换图/成组保持同一次插入身份，完成先后与显示层级不改变生成顺序。 */
export function inheritCanvasInsertionOrder(source: CanvasInsertionObject, target: CanvasInsertionObject): void {
  const order = getCanvasInsertionOrder(source);
  if (order) stampInsertionOrder(target, order);
}
